import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as queries from '../../graphql/queries';
import { graphqlClient } from '../../services/graphqlClient';
import {
  runConsistencyVerification,
  runDocumentVerification,
  runGuidelineReview,
} from '../../services/guidelinesApiService';
import type { Document, GuidelineRecord, Loan } from '../../graphql/types';
import { JobType } from '../../graphql/types';
import type { CheckResults, GuidelineFindingInput } from './analyzeModel';

/**
 * Data calls for Analyze mode, reusing the routes and queries the legacy
 * `DocumentVerificationStep` and `InvestorGuidelineReviewStep` use:
 *
 * - run 1: POST /guidelines/document_verification + /consistency_verification
 * - run 2: POST /guidelines/review
 * - cached results: the `get…Results` GraphQL queries
 *
 * Errors thrown from here carry plain-language messages; raw API text is
 * logged, not shown.
 */

const client = generateClient();

async function getTenantId(): Promise<string> {
  const session = await fetchAuthSession();
  return (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
}

export interface AnalyzeContext {
  tenantId: string;
  loans: Loan[];
  documents: Document[];
}

/** Loans and documents of the job, following pagination. */
export async function loadAnalyzeContext(jobId: string): Promise<AnalyzeContext> {
  const tenantId = await getTenantId();

  const loans: Loan[] = [];
  let loanToken: string | undefined;
  do {
    const page = await graphqlClient.queries.listLoansByJob(jobId, tenantId, undefined, loanToken);
    loans.push(...page.items);
    loanToken = page.nextToken ?? undefined;
  } while (loanToken);

  const documents: Document[] = [];
  let docToken: string | undefined;
  do {
    const page = await graphqlClient.queries.listDocumentsByJob(jobId, tenantId, undefined, docToken);
    documents.push(...page.items);
    docToken = page.nextToken ?? undefined;
  } while (docToken);

  return { tenantId, loans, documents };
}

/** Guidelines the tenant can apply; BA jobs only see "Borrower Analysis" guidelines. */
export async function loadGuidelines(
  tenantId: string,
  jobType: JobType.DD | JobType.BA
): Promise<GuidelineRecord[]> {
  return graphqlClient.queries.listGuidelines(
    tenantId,
    jobType === JobType.BA ? 'Borrower Analysis' : undefined
  );
}

export interface StoredAnalysis {
  completeness: CheckResults | null;
  consistency: CheckResults | null;
  guideline: GuidelineFindingInput[] | null;
}

async function queryResults<T>(
  query: string,
  variables: Record<string, unknown>,
  field: string
): Promise<T | null> {
  try {
    const response = (await client.graphql({ query, variables })) as {
      data?: Record<string, T | null>;
    };
    return response.data?.[field] ?? null;
  } catch (err) {
    // A missing cache is normal: the run has not happened yet.
    console.warn(`[AnalyzeMode] ${field} not available:`, err);
    return null;
  }
}

function toCheckResults<T extends { status: boolean; reason?: string | null }>(
  rows: ReadonlyArray<T> | undefined,
  nameOf: (row: T) => string
): CheckResults | null {
  // An empty list is what a run that never happened looks like.
  if (!rows || rows.length === 0) return null;
  const result: CheckResults = {};
  for (const row of rows) {
    result[nameOf(row)] = { status: row.status, reason: row.reason ?? undefined };
  }
  return result;
}

/** Results stored by earlier runs for the job and guideline. */
export async function loadStoredAnalysis(
  jobId: string,
  tenantId: string,
  guidelineId: string
): Promise<StoredAnalysis> {
  const variables = { jobId, tenantId, guidelineId };
  const [completeness, consistency, guideline] = await Promise.all([
    queryResults<{ results?: Array<{ documentName: string; status: boolean; reason?: string }> }>(
      queries.getDocumentVerificationResults,
      variables,
      'getDocumentVerificationResults'
    ),
    queryResults<{ results?: Array<{ checkName: string; status: boolean; reason?: string }> }>(
      queries.getConsistencyVerificationResults,
      variables,
      'getConsistencyVerificationResults'
    ),
    queryResults<{
      findings?: Array<{
        loanId: string;
        status: string;
        explanation: string;
        unmetCriteria?: string[] | null;
      }>;
    }>(queries.getGuidelineReviewResults, variables, 'getGuidelineReviewResults'),
  ]);

  return {
    completeness: toCheckResults(completeness?.results, (r) => r.documentName),
    consistency: toCheckResults(consistency?.results, (r) => r.checkName),
    guideline: guideline?.findings
      ? guideline.findings.map((f) => ({
          loanId: f.loanId,
          status: f.status,
          explanation: f.explanation,
          unmetCriteria: f.unmetCriteria ?? undefined,
        }))
      : null,
  };
}

export interface ChecksRunResult {
  completeness: CheckResults | null;
  consistency: CheckResults | null;
  /** Which of the two checks could not run, for the message under the button. */
  failed: Array<'completeness' | 'consistency'>;
}

/**
 * Run 1: completeness and consistency as one pass. Both requests are sent
 * together; if only one fails the other still returns. Throws only when both
 * fail.
 */
export async function runChecks(jobId: string, guidelineId: string): Promise<ChecksRunResult> {
  const req = { guideline_id: guidelineId, job_id: jobId };
  const [completeness, consistency] = await Promise.allSettled([
    runDocumentVerification(req),
    runConsistencyVerification(req),
  ]);

  if (completeness.status === 'rejected') {
    console.error('[AnalyzeMode] Completeness check failed:', completeness.reason);
  }
  if (consistency.status === 'rejected') {
    console.error('[AnalyzeMode] Consistency check failed:', consistency.reason);
  }
  if (completeness.status === 'rejected' && consistency.status === 'rejected') {
    throw new Error('The checks could not run. Check your connection and try again.');
  }

  const failed: ChecksRunResult['failed'] = [];
  if (completeness.status === 'rejected') failed.push('completeness');
  if (consistency.status === 'rejected') failed.push('consistency');
  return {
    completeness: completeness.status === 'fulfilled' ? completeness.value : null,
    consistency: consistency.status === 'fulfilled' ? consistency.value : null,
    failed,
  };
}

/**
 * Run 2: guideline review. The route reviews every loan in the job and does not
 * take a scope (design.md, Backend gaps); the caller limits what it shows to
 * the scope the user chose.
 */
export async function runReview(
  jobId: string,
  guidelineId: string
): Promise<GuidelineFindingInput[]> {
  try {
    const result = await runGuidelineReview({ guideline_id: guidelineId, job_id: jobId });
    return result.findings.map((f) => ({
      loanId: f.loan_id,
      status: f.status,
      explanation: f.explanation,
      unmetCriteria: f.unmet_criteria,
    }));
  } catch (err) {
    console.error('[AnalyzeMode] Guideline review failed:', err);
    throw new Error('The guideline review could not run. Check your connection and try again.');
  }
}
